import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ImageUploader from "@/components/admin/ImageUploader";
import GalleryUploader from "@/components/admin/GalleryUploader";
import VideoUrlList from "@/components/admin/VideoUrlList";
import RichTextEditor from "@/components/admin/RichTextEditor";
import { ProfileDetailsEditor, type QAItem, type BookingCTA } from "@/components/athlete/ProfileFieldEditors";
import NotificationsBell from "@/components/NotificationsBell";
import { publicUrl } from "@/lib/storage";
import { toast } from "sonner";
import AccountSettings from "@/components/AccountSettings";
import RichText from "@/components/RichText";
import TransactionsPanel from "@/components/support/TransactionsPanel";
import type { SupportTransaction } from "@/lib/support";
import AthleteHubHome from "@/components/athlete/AthleteHubHome";

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
const STATUS_MAX = 500;

type Athlete = {
  id: string;
  slug: string;
  name: string;
  sport: string;
  country: string | null;
  tagline: string | null;
  bio: string | null;
  image_path: string | null;
  gallery_paths: string[];
  video_urls: string[];
  stats: { label: string; value: string }[];
  published: boolean;
  instagram_url: string | null;
  tiktok_url: string | null;
  facebook_url: string | null;
  youtube_url: string | null;
  linkedin_url: string | null;
  custom_links: { label: string; url: string }[];
  jersey_number: string | null;
  position_short: string | null;
  position_long: string | null;
  height: string | null;
  hometown: string | null;
  college: string | null;
  occupation: string | null;
  years_playing: number | null;
  throws_hand: string | null;
  high_school: string | null;
  team_logo_path: string | null;
  mantra_quote: string | null;
  support_blurb: string | null;
  qa: QAItem[];
  booking_ctas: BookingCTA[];
  booking_inquiry_email: string | null;
};

type Cause = {
  id: string;
  athlete_id: string | null;
  name: string;
  blurb: string | null;
  goal: number;
  raised: number;
  donate_url: string | null;
  published: boolean;
};

type Product = {
  id: string;
  athlete_id: string | null;
  brand_name: string;
  product_name: string;
  category: string | null;
  price: string | null;
  product_url: string | null;
  quote: string | null;
  image_path: string | null;
  published: boolean;
};

const tabs = ["Home", "Profile", "Status", "Causes", "Products", "Support", "Account"] as const;
type Tab = (typeof tabs)[number];

const AthleteDashboard = () => {
  const { user, isAthlete, isAdmin, loading, signOut } = useAuth();
  const [athlete, setAthlete] = useState<Athlete | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [tab, setTab] = useState<Tab>("Home");

  const [statusMsg, setStatusMsg] = useState("");
  const [savedStatus, setSavedStatus] = useState<{ message: string; updated_at: string } | null>(null);

  const [supportRows, setSupportRows] = useState<SupportTransaction[]>([]);
  const [loadingSupport, setLoadingSupport] = useState(false);

  const [causes, setCauses] = useState<Cause[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [editingCause, setEditingCause] = useState<Partial<Cause> | null>(null);
  const [editingProduct, setEditingProduct] = useState<Partial<Product> | null>(null);

  useEffect(() => {
    if (!athlete?.id) return;
    let cancelled = false;
    setLoadingSupport(true);
    supabase
      .from("support_transactions")
      .select("id,athlete_id,supporter_name,supporter_email,amount,currency,transaction_type,message,is_public,is_recurring,transaction_timestamp")
      .eq("athlete_id", athlete.id)
      .order("transaction_timestamp", { ascending: false })
      .limit(500)
      .then(({ data }) => {
        if (cancelled) return;
        setSupportRows(((data ?? []) as unknown) as SupportTransaction[]);
        setLoadingSupport(false);
      });
    return () => { cancelled = true; };
  }, [athlete?.id]);

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoadingProfile(true);
      setLoadError(null);
      try {
        const fetchAthlete = async () =>
          supabase.from("athletes").select("*").eq("owner_user_id", user.id).maybeSingle();

        let { data: a, error: aErr } = await fetchAthlete();
        if (aErr) throw aErr;

        // Auto-create a draft profile on first login if none is linked yet.
        if (!a) {
          const { error: rpcErr } = await supabase.rpc("ensure_athlete_profile" as any);
          if (rpcErr) throw rpcErr;
          const retry = await fetchAthlete();
          if (retry.error) throw retry.error;
          a = retry.data;
        }
        if (cancelled) return;
        if (!a) {
          setAthlete(null);
          setLoadingProfile(false);
          return;
        }
        const normalized: any = {
          ...(a as any),
          custom_links: Array.isArray((a as any).custom_links) ? (a as any).custom_links : [],
          stats: Array.isArray((a as any).stats) ? (a as any).stats : [],
          gallery_paths: (a as any).gallery_paths ?? [],
          video_urls: (a as any).video_urls ?? [],
          qa: Array.isArray((a as any).qa) ? (a as any).qa : [],
          booking_ctas: Array.isArray((a as any).booking_ctas) ? (a as any).booking_ctas : [],
        };
        setAthlete(normalized);

        const [s, cs, ps] = await Promise.all([
          supabase.from("athlete_status").select("message, updated_at").eq("athlete_id", normalized.id).maybeSingle(),
          supabase.from("causes").select("*").eq("athlete_id", normalized.id).order("created_at", { ascending: false }),
          supabase.from("brand_products").select("*").eq("athlete_id", normalized.id).order("created_at", { ascending: false }),
        ]);
        if (cancelled) return;
        setSavedStatus(s.data as any);
        setStatusMsg((s.data as any)?.message ?? "");
        setCauses((cs.data as any) ?? []);
        setProducts((ps.data as any) ?? []);
      } catch (err: any) {
        console.error("Dashboard load error:", err);
        const message = err?.message ?? "Failed to load dashboard";
        setLoadError(message);
        toast.error(message);
      } finally {
        if (!cancelled) setLoadingProfile(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loading, user, reloadKey]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/athlete-login" state={{ from: "/dashboard" }} replace />;

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center text-muted-foreground">Loading your profile…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <div className="max-w-md">
            <h1 className="font-display text-3xl">Dashboard unavailable</h1>
            <p className="mt-3 text-muted-foreground">{loadError}</p>
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
              <button onClick={() => setReloadKey((key) => key + 1)} className="px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold">
                Try again
              </button>
              <button onClick={signOut} className="px-5 py-2 rounded-full bg-muted text-sm font-semibold">Sign out</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!athlete) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <div className="max-w-md">
            <h1 className="font-display text-3xl">Profile not linked</h1>
            <p className="mt-3 text-muted-foreground">
              Your athlete profile hasn't been linked to your account yet. Please contact the team.
            </p>
            <div className="mt-6">
              <button onClick={signOut} className="px-5 py-2 rounded-full bg-muted text-sm font-semibold">Sign out</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const saveProfile = async () => {
    const payload: any = {
      sport: athlete.sport,
      country: athlete.country,
      tagline: athlete.tagline,
      bio: athlete.bio,
      image_path: athlete.image_path,
      gallery_paths: athlete.gallery_paths ?? [],
      video_urls: (athlete.video_urls ?? []).filter((u) => u && u.trim()),
      stats: athlete.stats,
      instagram_url: athlete.instagram_url || null,
      tiktok_url: athlete.tiktok_url || null,
      facebook_url: athlete.facebook_url || null,
      youtube_url: athlete.youtube_url || null,
      linkedin_url: athlete.linkedin_url || null,
      custom_links: (athlete.custom_links ?? []).filter((l) => l.label?.trim() && l.url?.trim()),
      jersey_number: athlete.jersey_number || null,
      position_short: athlete.position_short || null,
      position_long: athlete.position_long || null,
      height: athlete.height || null,
      hometown: athlete.hometown || null,
      college: athlete.college || null,
      occupation: athlete.occupation || null,
      years_playing: athlete.years_playing ?? null,
      throws_hand: athlete.throws_hand || null,
      high_school: athlete.high_school || null,
      team_logo_path: athlete.team_logo_path ?? null,
      mantra_quote: athlete.mantra_quote || null,
      support_blurb: athlete.support_blurb || null,
      qa: (athlete.qa ?? []).filter((q) => q.question?.trim() && q.answer?.trim()),
      booking_ctas: (athlete.booking_ctas ?? []).filter((c) => c.label?.trim() && c.url?.trim()),
      booking_inquiry_email: athlete.booking_inquiry_email || null,
    };
    const { error } = await supabase.from("athletes").update(payload).eq("id", athlete.id);
    if (error) return toast.error(error.message);
    toast.success("Profile saved — admins have been notified");
  };

  const postStatus = async () => {
    const plain = stripHtml(statusMsg);
    if (!plain) return toast.error("Status cannot be empty");
    if (plain.length > STATUS_MAX) return toast.error(`Max ${STATUS_MAX} characters`);
    const { error } = await supabase
      .from("athlete_status")
      .upsert({ athlete_id: athlete.id, message: statusMsg, published: true, updated_at: new Date().toISOString() });
    if (error) return toast.error(error.message);
    setSavedStatus({ message: statusMsg, updated_at: new Date().toISOString() });
    toast.success("Status updated");
  };

  const clearStatus = async () => {
    const { error } = await supabase.from("athlete_status").delete().eq("athlete_id", athlete.id);
    if (error) return toast.error(error.message);
    setSavedStatus(null);
    setStatusMsg("");
    toast.success("Status cleared");
  };

  const saveCause = async () => {
    if (!editingCause) return;
    if (!editingCause.name?.trim()) return toast.error("Name required");
    const payload: any = {
      athlete_id: athlete.id,
      name: editingCause.name,
      blurb: editingCause.blurb ?? null,
      goal: Number(editingCause.goal) || 0,
      raised: Number(editingCause.raised) || 0,
      donate_url: editingCause.donate_url ?? null,
      published: false,
    };
    const { error } = editingCause.id
      ? await supabase.from("causes").update(payload).eq("id", editingCause.id)
      : await supabase.from("causes").insert(payload);
    if (error) return toast.error(error.message);
    toast.success("Submitted for review");
    setEditingCause(null);
    const { data } = await supabase.from("causes").select("*").eq("athlete_id", athlete.id).order("created_at", { ascending: false });
    setCauses((data as any) ?? []);
  };

  const saveProduct = async () => {
    if (!editingProduct) return;
    if (!editingProduct.brand_name?.trim() || !editingProduct.product_name?.trim()) {
      return toast.error("Brand and product name are required");
    }
    const payload: any = {
      athlete_id: athlete.id,
      brand_name: editingProduct.brand_name,
      product_name: editingProduct.product_name,
      category: editingProduct.category ?? null,
      price: editingProduct.price ?? null,
      product_url: editingProduct.product_url ?? null,
      quote: editingProduct.quote ?? null,
      image_path: editingProduct.image_path ?? null,
      published: false,
    };
    const { error } = editingProduct.id
      ? await supabase.from("brand_products").update(payload).eq("id", editingProduct.id)
      : await supabase.from("brand_products").insert(payload);
    if (error) return toast.error(error.message);
    toast.success("Submitted for review");
    setEditingProduct(null);
    const { data } = await supabase.from("brand_products").select("*").eq("athlete_id", athlete.id).order("created_at", { ascending: false });
    setProducts((data as any) ?? []);
  };

  const charsLeft = STATUS_MAX - stripHtml(statusMsg).length;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="container py-10 flex-1">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Athlete Dashboard</p>
            <h1 className="font-display text-3xl md:text-4xl mt-1">{athlete.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {athlete.sport}{athlete.country ? ` · ${athlete.country}` : ""} ·{" "}
              <span className={athlete.published ? "text-primary font-semibold" : ""}>
                {athlete.published ? "Live" : "Draft (awaiting publish)"}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <NotificationsBell />
            <Link to={`/athlete/${athlete.slug}`} className="px-4 py-2 rounded-full bg-muted text-sm font-semibold">
              View public profile
            </Link>
          </div>
        </div>

        {(() => {
          const dismissKey = `athlete_welcome_dismissed_${athlete.id}`;
          const dismissed = typeof window !== "undefined" && localStorage.getItem(dismissKey) === "1";
          const isFreshDraft =
            !athlete.published &&
            !athlete.bio &&
            !athlete.image_path &&
            (athlete.sport === "TBD" || !athlete.sport);
          if (dismissed || !isFreshDraft) return null;
          return (
            <div className="bg-gradient-warm rounded-2xl p-6 md:p-7 shadow-card mb-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <div className="flex-1">
                <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Welcome 👋</p>
                <h2 className="font-display text-2xl md:text-3xl mt-1">This is your home base</h2>
                <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                  Customize your public profile, share what you're up to, spotlight the causes you care about,
                  and add the brands you love. Admins will publish your profile once you're ready.
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => { setTab("Profile"); document.querySelector('main')?.scrollTo?.({ top: 0 }); }}
                  className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-secondary transition-colors"
                >
                  Start editing
                </button>
                <button
                  onClick={() => { localStorage.setItem(dismissKey, "1"); setAthlete({ ...athlete }); }}
                  className="px-5 py-2.5 rounded-full bg-muted text-sm font-semibold"
                >
                  Dismiss
                </button>
              </div>
            </div>
          );
        })()}

        <div className="border-b border-border mb-6 flex gap-1 overflow-x-auto">

          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${
                tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "Home" && (
          <div className="max-w-3xl">
            <AthleteHubHome athlete={athlete as any} causeCount={causes.filter((c) => c.published).length} onGo={(t) => setTab(t)} />
          </div>
        )}

        {tab === "Profile" && (
          <div className="bg-card rounded-2xl p-6 shadow-card max-w-3xl space-y-5">
            <p className="text-xs text-muted-foreground">Name and slug are managed by admins. All other edits go live and admins are notified.</p>
            <ImageUploader value={athlete.image_path} onChange={(p) => setAthlete({ ...athlete, image_path: p })} prefix="athletes" />
            <p className="text-xs text-muted-foreground -mt-2">Recommended: square, at least 800×800px. JPG or PNG, under 5MB.</p>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Sport"><input className={inp} value={athlete.sport} onChange={(e) => setAthlete({ ...athlete, sport: e.target.value })} maxLength={50} /></Field>
              <Field label="Country"><input className={inp} value={athlete.country ?? ""} onChange={(e) => setAthlete({ ...athlete, country: e.target.value })} maxLength={50} /></Field>
            </div>

            <Field label="Tagline"><input className={inp} value={athlete.tagline ?? ""} onChange={(e) => setAthlete({ ...athlete, tagline: e.target.value })} maxLength={200} /></Field>

            <Field label="Bio">
              <RichTextEditor value={athlete.bio ?? ""} onChange={(v) => setAthlete({ ...athlete, bio: v })} />
            </Field>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider">Team logo override</label>
              <div className="mt-1">
                <ImageUploader value={athlete.team_logo_path ?? null} onChange={(p) => setAthlete({ ...athlete, team_logo_path: p })} prefix="athletes/team-logo" />
              </div>
            </div>

            <ProfileDetailsEditor
              value={athlete}
              onChange={(patch) => setAthlete({ ...athlete, ...patch } as Athlete)}
            />

            <Field label={`Gallery — ${(athlete.gallery_paths ?? []).length}/10`}>
              <GalleryUploader value={athlete.gallery_paths ?? []} onChange={(paths) => setAthlete({ ...athlete, gallery_paths: paths.slice(0, 10) })} prefix="athletes" />
              <p className="text-xs text-muted-foreground mt-2">Recommended: landscape 1600×1200px (4:3) or 1920×1080 (16:9). Up to 10 images, under 5MB each.</p>
            </Field>

            <Field label={`Videos — ${(athlete.video_urls ?? []).length}/3`}>
              <VideoUrlList value={athlete.video_urls ?? []} onChange={(urls) => setAthlete({ ...athlete, video_urls: urls })} max={3} />
              <p className="text-xs text-muted-foreground mt-2">Paste YouTube, Vimeo, or TikTok links. Landscape 1920×1080 (16:9) works best.</p>
            </Field>

            <div className="rounded-xl bg-muted/40 border border-border p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider">Social links</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Instagram"><input className={inp} value={athlete.instagram_url ?? ""} onChange={(e) => setAthlete({ ...athlete, instagram_url: e.target.value })} placeholder="https://instagram.com/..." /></Field>
                <Field label="TikTok"><input className={inp} value={athlete.tiktok_url ?? ""} onChange={(e) => setAthlete({ ...athlete, tiktok_url: e.target.value })} placeholder="https://tiktok.com/@..." /></Field>
                <Field label="Facebook"><input className={inp} value={athlete.facebook_url ?? ""} onChange={(e) => setAthlete({ ...athlete, facebook_url: e.target.value })} /></Field>
                <Field label="YouTube"><input className={inp} value={athlete.youtube_url ?? ""} onChange={(e) => setAthlete({ ...athlete, youtube_url: e.target.value })} /></Field>
                <Field label="LinkedIn"><input className={inp} value={athlete.linkedin_url ?? ""} onChange={(e) => setAthlete({ ...athlete, linkedin_url: e.target.value })} placeholder="https://linkedin.com/in/..." /></Field>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider">Stats (max 6)</label>
              <div className="space-y-2 mt-2">
                {athlete.stats.map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <input className={inp} value={s.label} onChange={(e) => { const next = [...athlete.stats]; next[i] = { ...s, label: e.target.value }; setAthlete({ ...athlete, stats: next }); }} placeholder="Label" maxLength={30} />
                    <input className={inp} value={s.value} onChange={(e) => { const next = [...athlete.stats]; next[i] = { ...s, value: e.target.value }; setAthlete({ ...athlete, stats: next }); }} placeholder="Value" maxLength={30} />
                    <button onClick={() => setAthlete({ ...athlete, stats: athlete.stats.filter((_, j) => j !== i) })} className="px-3 text-destructive">✕</button>
                  </div>
                ))}
                {athlete.stats.length < 6 && (
                  <button onClick={() => setAthlete({ ...athlete, stats: [...athlete.stats, { label: "", value: "" }] })} className="text-sm text-primary font-semibold">+ Add stat</button>
                )}
              </div>
            </div>

            <button onClick={saveProfile} className="px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary transition-colors">
              Save profile
            </button>
          </div>
        )}

        {tab === "Status" && (
          <div className="bg-card rounded-2xl p-6 shadow-card max-w-2xl">
            <h2 className="font-display text-2xl">Published athlete update</h2>
            <p className="text-sm text-muted-foreground mt-1">A short update shown on your public profile and in My Corner for followers who opted in.</p>
            <div className="mt-4">
              <RichTextEditor value={statusMsg} onChange={setStatusMsg} />
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className={`text-xs ${charsLeft < 40 ? "text-destructive" : "text-muted-foreground"}`}>{charsLeft} characters left</span>
              <div className="flex gap-2">
                {savedStatus && (
                  <button onClick={clearStatus} className="px-4 py-2 rounded-full bg-muted text-sm font-semibold">Clear</button>
                )}
                <button onClick={postStatus} className="px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold">Post status</button>
              </div>
            </div>
            {savedStatus && (
              <div className="mt-6 rounded-2xl bg-muted/40 border border-border p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Live now</p>
                <RichText html={savedStatus.message} className="mt-1 text-base" />
                <p className="text-xs text-muted-foreground mt-2">Posted {new Date(savedStatus.updated_at).toLocaleString()}</p>
              </div>
            )}
          </div>
        )}

        {tab === "Causes" && (
          <div className="space-y-4 max-w-3xl">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl">Your causes</h2>
              <button onClick={() => setEditingCause({ name: "", goal: 0, raised: 0 })} className="px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold">+ Submit a cause</button>
            </div>
            <p className="text-sm text-muted-foreground">Submissions are saved as drafts and reviewed before going live.</p>
            <div className="grid gap-3">
              {causes.map((c) => (
                <div key={c.id} className="bg-card rounded-2xl p-4 flex items-center gap-4 shadow-card">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold">{c.name}</p>
                      <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${c.published ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                        {c.published ? "Live" : "Pending review"}
                      </span>
                    </div>
                    {c.blurb && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.blurb}</p>}
                  </div>
                  {!c.published && (
                    <button onClick={() => setEditingCause(c)} className="px-4 py-2 rounded-full bg-muted text-sm font-semibold">Edit</button>
                  )}
                </div>
              ))}
              {causes.length === 0 && <p className="text-muted-foreground text-sm">No causes yet.</p>}
            </div>
          </div>
        )}

        {tab === "Products" && (
          <div className="space-y-4 max-w-3xl">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl">Your products</h2>
              <button onClick={() => setEditingProduct({ brand_name: "", product_name: "" })} className="px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold">+ Submit a product</button>
            </div>
            <p className="text-sm text-muted-foreground">Submissions are saved as drafts and reviewed before going live.</p>
            <div className="grid gap-3">
              {products.map((p) => (
                <div key={p.id} className="bg-card rounded-2xl p-4 flex items-center gap-4 shadow-card">
                  <div className="w-14 h-14 rounded-xl bg-muted overflow-hidden shrink-0">
                    {p.image_path && <img src={publicUrl(p.image_path)!} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold truncate">{p.brand_name} — {p.product_name}</p>
                      <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${p.published ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                        {p.published ? "Live" : "Pending review"}
                      </span>
                    </div>
                    {p.category && <p className="text-sm text-muted-foreground">{p.category}{p.price ? ` · ${p.price}` : ""}</p>}
                  </div>
                  {!p.published && (
                    <button onClick={() => setEditingProduct(p)} className="px-4 py-2 rounded-full bg-muted text-sm font-semibold">Edit</button>
                  )}
                </div>
              ))}
              {products.length === 0 && <p className="text-muted-foreground text-sm">No products yet.</p>}
            </div>
          </div>
        )}

        {tab === "Support" && (
          <div className="space-y-4">
            <div className="bg-card rounded-2xl p-5 shadow-card">
              <p className="font-display text-xl">Your support</p>
              <p className="text-sm text-muted-foreground mt-1">
                Support paid directly to you by fans. These are your own records for bookkeeping — not tax records or tax advice.
              </p>
            </div>
            <TransactionsPanel
              rows={supportRows}
              loading={loadingSupport}
              csvFilename="my-support-transactions.csv"
              emptyLabel="No support received yet."
            />
          </div>
        )}

        {tab === "Account" && (
          <div className="bg-card rounded-2xl p-6 shadow-card max-w-2xl">
            <p className="text-xs text-muted-foreground mb-4">These settings change your login credentials, not your public athlete profile.</p>
            <AccountSettings />
          </div>
        )}
      </main>
      <Footer />

      {editingCause && (
        <Modal title={editingCause.id ? "Edit cause" : "Submit a cause"} onClose={() => setEditingCause(null)}>
          <Field label="Name *"><input className={inp} value={editingCause.name ?? ""} onChange={(e) => setEditingCause({ ...editingCause, name: e.target.value })} maxLength={100} /></Field>
          <Field label="Blurb"><textarea className={inp} rows={3} value={editingCause.blurb ?? ""} onChange={(e) => setEditingCause({ ...editingCause, blurb: e.target.value })} maxLength={500} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Goal ($)"><input type="number" className={inp} value={editingCause.goal ?? 0} onChange={(e) => setEditingCause({ ...editingCause, goal: Number(e.target.value) || 0 })} /></Field>
            <Field label="Raised ($)"><input type="number" className={inp} value={editingCause.raised ?? 0} onChange={(e) => setEditingCause({ ...editingCause, raised: Number(e.target.value) || 0 })} /></Field>
          </div>
          <Field label="Donate URL"><input className={inp} value={editingCause.donate_url ?? ""} onChange={(e) => setEditingCause({ ...editingCause, donate_url: e.target.value })} placeholder="https://" /></Field>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setEditingCause(null)} className="px-5 py-2.5 rounded-full bg-muted font-semibold">Cancel</button>
            <button onClick={saveCause} className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">Submit for review</button>
          </div>
        </Modal>
      )}

      {editingProduct && (
        <Modal title={editingProduct.id ? "Edit product" : "Submit a product"} onClose={() => setEditingProduct(null)}>
          <ImageUploader value={editingProduct.image_path ?? null} onChange={(p) => setEditingProduct({ ...editingProduct, image_path: p })} prefix="products" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Brand *"><input className={inp} value={editingProduct.brand_name ?? ""} onChange={(e) => setEditingProduct({ ...editingProduct, brand_name: e.target.value })} maxLength={100} /></Field>
            <Field label="Product *"><input className={inp} value={editingProduct.product_name ?? ""} onChange={(e) => setEditingProduct({ ...editingProduct, product_name: e.target.value })} maxLength={120} /></Field>
            <Field label="Category"><input className={inp} value={editingProduct.category ?? ""} onChange={(e) => setEditingProduct({ ...editingProduct, category: e.target.value })} maxLength={60} /></Field>
            <Field label="Price"><input className={inp} value={editingProduct.price ?? ""} onChange={(e) => setEditingProduct({ ...editingProduct, price: e.target.value })} maxLength={30} /></Field>
          </div>
          <Field label="Product URL"><input className={inp} value={editingProduct.product_url ?? ""} onChange={(e) => setEditingProduct({ ...editingProduct, product_url: e.target.value })} placeholder="https://" /></Field>
          <Field label="Quote"><textarea className={inp} rows={3} value={editingProduct.quote ?? ""} onChange={(e) => setEditingProduct({ ...editingProduct, quote: e.target.value })} maxLength={500} /></Field>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setEditingProduct(null)} className="px-5 py-2.5 rounded-full bg-muted font-semibold">Cancel</button>
            <button onClick={saveProduct} className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">Submit for review</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const inp = "w-full px-3 py-2 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary text-sm";

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const Modal = ({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) => (
  <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto">
    <div className="bg-card rounded-[1.5rem] shadow-bold max-w-xl w-full p-6 my-8 space-y-3">
      <div className="flex justify-between items-center mb-2">
        <h2 className="font-display text-2xl">{title}</h2>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
      </div>
      {children}
    </div>
  </div>
);

export default AthleteDashboard;
